import "twin.macro";

import { useSelector } from "react-redux";
import { Link } from "react-router";

import { format } from "date-fns";

import IndexTable, {
  Column,
  PaginatedResourceProps,
} from "@features/ui/indexTable";
import SearchableText from "@features/ui/indexTable/SearchableText";
import { Order } from "@models";
import {
  formatDateString,
  formatMoneyString,
  upCase,
} from "@utility/utilityFunctions";

import calculateOrderTotal from "../calculateOrderTotal";
import OrderStatus from "./OrderStatus";

const insertIf = (cond, val) => (cond ? [val] : []);

const OrderHistoryOrderTable = ({
  query,
  ...tableProps
}: PaginatedResourceProps & {
  rows: Order[];
  query?: string;
}) => {
  const { usesCostCenters, budgetLocation, ...organization } = useSelector(
    (state: any) => state.currentUser.organization
  );
  const columns: Column<Order>[] = [
    {
      id: "status",
      label: "Status",
      sort: "status",
      to: (row) => `/orders/history/${row.id}`,
      render: (status) => <OrderStatus status={status} />,
    },
    {
      id: "id",
      label: "Order #",
      sort: "id",
      reverseSort: true,
      to: (row) => `/orders/history/${row.id}`,
      render: (id) => (
        <Link to={`/orders/history/${id}`} tw="hover:underline font-semibold">
          <SearchableText text={id} query={query} />
        </Link>
      ),
    },
    {
      id: "type",
      label: "Order Type",
      to: (row) => `/orders/history/${row.id}`,
      render: (ot) => upCase(ot, "-"),
    },
    { id: "userName", label: "Ordered By" },
    {
      id: "address.name",
      label: "Address Name",
      sort: "address-name",
      render: (name) => <SearchableText text={name} query={query} />,
    },
    {
      id: "state",
      label: "State / Region",
      sort: "state",
      render: (_, row) => row.address.state?.code ?? row.address.region,
    },
    {
      id: "programName",
      label: "Program",
      render: (program) => <div tw="w-max max-w-xs">{program}</div>,
    },
    {
      id: "submittedAt",
      label: "Order Date",
      sort: "submitted-at",
      reverseSort: true,
      render: (date) => (date ? format(new Date(date), "MM/dd/yyyy") : ""),
    },
    {
      id: "approvedAt",
      label: "Approved Date",
      sort: "approved-at",
      reverseSort: true,
      render: (date) => (date ? format(new Date(date), "MM/dd/yyyy") : ""),
    },
    {
      id: "expeditedDate",
      label: "Requested Date",
      render: (d) => (d ? formatDateString(d) : ""),
    },
    { id: "totalQuantity", label: "QTY", align: "right" },
    {
      id: "totalPromotionDiscount",
      label: "Promotion Discount",
      align: "right",
      render: formatMoneyString,
    },
    {
      id: "totalPrice",
      label: "Extended Price",
      align: "right",
      render: formatMoneyString,
    },
    {
      id: "orderTotal",
      label: "Order Total",
      align: "right",
      render: (_, order) =>
        formatMoneyString(calculateOrderTotal(order, organization)),
    },
    { id: "checkoutType", label: "Payment Type", render: upCase },
    ...insertIf(budgetLocation, {
      id: "budgetNames",
      label: "Budget",
      render: (names) => names?.join(", "),
    }),
    ...insertIf(usesCostCenters, {
      id: "costCenterName",
      label: "Cost Center",
    }),
  ];

  return <IndexTable {...tableProps} columns={columns} />;
};

export default OrderHistoryOrderTable;
